import React, { createContext, useContext, useEffect, useState } from "react";
import { diseaseAPI } from "../apis";
import { healthScoreGroups } from "../utils/constants";
import {
  convertResultTo2DArray,
  getReviewAnswerFormattedOutput,
  getSectionType,
  processQuestions,
  transformQuestions,
} from "../utils/eira/helper";
import { getVikritiRecommendations } from "../apis/apiCall";
import { useAppState } from "./app-state";
import { authenticateApp } from "./auth-context";

// Context Type Definition
interface IQuestionContext {
  selectedScore: string | null;
  questions: any[];
  mappedList: any;
  setMappedList: (value: any) => void;
  questionMap: any;
  questionArrayLength: number;
  currentSectionIndex: number;
  currentQuestionIndex: number;
  currentQuestionDisplay: number;
  isDialogOpen: boolean;
  setSelectedScore: (score: string | null) => void;
  handleSelectScore: (score: string) => void;
  userScores: any;
  setUserScores: (value: any) => void;
  scoreResult: any | null;
  setScoreResult: (value: any | null) => void;
  scoreResultLoading: boolean;
  setScoreResultLoading: (value: boolean) => void;
  handleScoreReult: (userId: string, scoreName: string) => Promise<void>;
  handleMoveNextQuestion: (
    response: any,
    currentSecIndex: number,
    currentQuesIndex: number
  ) => Promise<void>;
  loading: boolean;
  setLoading: (value: boolean) => void;
  handleMovePreviousQuestion: (
    currentSecIndex: number,
    currentQuesIndex: number
  ) => Promise<void>;
  showResultDialog: boolean;
  setShowResultDialog: (value: boolean) => void;
  healthScores: any;
  setHealthScores: (value: any) => void;
  allScoresData: any;
  setAllScoresData: (value: any) => void;
  isInitialLoad: boolean;
  setIsInitialLoad: (value: boolean) => void;
  markScoreAsFilled: (scoreName: string) => void;
  healthHistoryCount: number;
  setHealthHistoryCount: (value: number) => void;
  nextScore: string | null;
  setNextScore: (value: string | null) => void;
  getNextScore: () => void;
  reviewResponse: any;
  setReviewResponse: (value: any) => void;
  scoreLabelCalledFor: string | null;
  setScoreLabelCalledFor: (value: string | null) => void;
  callFunction: (() => Promise<void>) | null;
  setCallFunction: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
  refetchScore: boolean;
  setRefetchScore: (value: boolean) => void;
}

export const questionContext = createContext<IQuestionContext>(
  {} as IQuestionContext
);

const useScoresStateProvider = () => {
  const { clinicUserId: userId } = authenticateApp();
  const [userScores, setUserScores] = useState<any>([]);
  const [selectedScore, setSelectedScore] = useState<string | null>(null);
  const [questions, setQuestions] = useState<any[]>([]);
  const [mappedList, setMappedList] = useState<any>({});
  const [questionMap, setQuestionMap] = useState<any>({});
  const [questionArrayLength, setQuestionArrayLength] = useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestionDisplay, setCurrentQuestionDisplay] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [scoreResult, setScoreResult] = useState<any | null>(null); // This stores the recommended score which can be dynamic and based on this we will have questionResponse.
  const [scoreResultLoading, setScoreResultLoading] = useState<boolean>(false); // This stores the recommended score which can be dynamic and based on this we will have questionResponse.
  const [loading, setLoading] = useState(false); // ⏳ Loading state for API calls
  const [showResultDialog, setShowResultDialog] = useState(false);
  // 🚀 Handle Initial Load when user selects a score
  const [healthScores, setHealthScores] = useState(healthScoreGroups);
  const [allScoresData, setAllScoresData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load
  const [healthHistoryCount, setHealthHistoryCount] = useState<number>(0);
  const [nextScore, setNextScore] = useState<string | null>(null);
  const [scoreLabelCalledFor, setScoreLabelCalledFor] = useState<string | null>(
    null
  );
  const [callFunction, setCallFunction] = useState<
    (() => Promise<void>) | null
  >(null);
  const [refetchScore, setRefetchScore] = useState(false);

  // review response
  const [reviewResponse, setReviewResponse] = useState<any>(null);

  useEffect(() => {
    const fetchScoreLabels = async () => {
      if (scoreLabelCalledFor) {
        try {
          await diseaseAPI.getScoreLabels(userId!, scoreLabelCalledFor);
        } catch (error) {
          console.error("Error while calling getScoreLabels API:", error);
        } finally {
          setScoreLabelCalledFor(null);
        }
      }
    };

    fetchScoreLabels();
  }, [scoreLabelCalledFor, userId]);

  // Correct type definition for an async function
  useEffect(() => {
    // Only execute if callFunction is not null
    if (callFunction) {
      const call = async () => {
        try {
          await callFunction();
          setCallFunction(null);
        } catch (error) {
          console.error("Error executing function:", error);
          setCallFunction(null);
        }
      };

      call();
    }
  }, [callFunction]);

  const fetchScores = async (userId: string) => {
    try {
      const response = await diseaseAPI.getAllScores(userId);
      // const response = await diseaseAPI.getAllScores("test001");

      if (response && response.recommendations) {
        const updatedHealthScores = healthScoreGroups.map((group) =>
          group.map((score) => {
            // Check if the score exists in the response
            const scoreData = response.recommendations[score.name];

            // If scoreData is found and has recommendations, set hasFilled = true
            const hasFilled = !!(
              scoreData &&
              scoreData.recommendations &&
              Object.keys(scoreData.recommendations).length > 0 &&
              scoreData.question_responses &&
              scoreData.questions &&
              scoreData.question_responses.length === scoreData.questions.length
            );

            const hasPartiallyFilled = !!(
              scoreData &&
              scoreData.recommendations &&
              Object.keys(scoreData.recommendations).length > 0 &&
              scoreData.question_responses &&
              scoreData.questions &&
              scoreData.question_responses.length < scoreData.questions.length
            );

            // Ensure hasFilled is always explicitly true/false
            return {
              ...score,
              hasFilled: hasFilled ? true : false,
              hasPartiallyFilled: hasPartiallyFilled ? true : false,
            };
          })
        );

        setHealthScores(updatedHealthScores);
        setAllScoresData(response.recommendations);
        setReviewResponse(
          getReviewAnswerFormattedOutput(response.recommendations)
        );
        setRefetchScore(false);
      }
    } catch (error) {
      console.error("Error fetching scores:", error);
      setRefetchScore(false);
    } finally {
      setIsInitialLoad(false); // This ensures it always runs at the end of fetchScores
    }
  };

  useEffect(() => {
    if (
      (isInitialLoad &&
        userId &&
        typeof userId === "string" &&
        userId.trim().length > 0) ||
      (userId && userId.trim().length > 0 && refetchScore)
    ) {
      fetchScores(userId);
    }
  }, [isInitialLoad, userId, refetchScore]);

  // Function to mark a score as filled manually (can trigger another effect if needed)
  const markScoreAsFilled = (scoreName: string) => {
    setHealthScores((prevScores) =>
      prevScores.map((group) =>
        group.map((score) =>
          score.name === scoreName ? { ...score, hasFilled: true } : score
        )
      )
    );
    setIsInitialLoad(true); // Optionally re-fetch data if necessary
  };

  const handleSelectScore = async (score: string) => {
    if (!userId || !score) return;

    setSelectedScore(score);
    setIsDialogOpen(true); // Open Dialog Box

    try {
      // Fetch Questions and Score Recommendations
      setScoreResult(null);
      setQuestions([]);
      setMappedList({});
      setCurrentSectionIndex(0);
      setCurrentQuestionIndex(0);
      setCurrentQuestionDisplay(1);
      const [questionData, recommendations] = await Promise.all([
        diseaseAPI.getScoreQuestions(userId, score),
        diseaseAPI.getAllScores(userId),
      ]);

      // Process Questions
      const { result, questionMap, mappedList, questionArrayLength } =
        processQuestions(questionData);

      const sorted2DArray = convertResultTo2DArray(result);
      const transformedQuestions = transformQuestions(
        sorted2DArray,
        questionMap
      );

      // Map responses if available
      // Map responses if available
      if (recommendations?.recommendations?.[score]?.question_responses) {
        recommendations.recommendations[score].question_responses.forEach(
          (resp: any) => {
            if (mappedList[resp.question_id] !== undefined) {
              const responseValue = resp.response_value; // Raw response

              // ✅ Case 1: If response is -1 (as a number), store it as [-1]
              if (responseValue === -1) {
                mappedList[resp.question_id] = [-1];
              }
              // ✅ Case 2: If response is a comma-separated string like "1,2,3"
              else if (
                typeof responseValue === "string" &&
                responseValue.includes(",")
              ) {
                mappedList[resp.question_id] = responseValue
                  .split(",")
                  .map(Number)
                  .filter((num) => !isNaN(num));
              }
              // ✅ Case 3: If response is a single number (not -1), store it as an array
              else if (!isNaN(Number(responseValue))) {
                mappedList[resp.question_id] = [Number(responseValue)];
              }
              // ✅ Case 4: If response is invalid, set an empty array (fallback)
              else {
                mappedList[resp.question_id] = [];
              }
            }
          }
        );
      }

      // Set state
      setQuestions(transformedQuestions);
      setMappedList(mappedList);
      setQuestionMap(questionMap);
      setQuestionArrayLength(questionArrayLength);
    } catch (error) {
      console.error("Error loading questions:", error);
    }
  };

  // // 🔄 Automatically Call `handleSelectScore` When `userId` and `selectedScore` Are Available
  useEffect(() => {
    if (userId && selectedScore) {
      handleSelectScore(selectedScore);
    }
  }, [userId, selectedScore]); // Runs when `userId` or `selectedScore` updates

  const handleMoveNextQuestion = async (
    responses: Record<string, number[]>,
    currentSecIndex: number,
    currentQuesIndex: number
  ) => {
    if (loading) return;
    setLoading(true);

    try {
      let currentSectionType = getSectionType(questions[currentSecIndex]);

      // ✅ Handle Normal Questions
      if (currentSectionType === "Normal") {
        const questionId =
          questions[currentSecIndex][currentQuesIndex].attributes.question_id;

        const transformedResponse = {
          questionnaire_id: questionId,
          response: JSON.stringify({
            value: responses[questionId]?.includes(-1)
              ? -1
              : responses[questionId]?.join(","),
          }),
          user_id: userId,
        };
        await diseaseAPI.saveScore(transformedResponse);

        if (currentQuesIndex === questions[currentSecIndex].length - 1) {
          if (currentSecIndex < questions.length - 1) {
            setCurrentQuestionIndex(0);
            setCurrentSectionIndex(currentSecIndex + 1);
            setCurrentQuestionDisplay(currentQuestionDisplay + 1);
          } else {
            await finalizeScore("Normal Questions");
          }
        } else {
          setCurrentQuestionIndex(currentQuesIndex + 1);
          setCurrentQuestionDisplay(currentQuestionDisplay + 1);
        }
      }

      // ✅ Handle SubQuestions (Chips)
      else if (currentSectionType === "SubQuestions") {
        const totalQuestions = questions[currentSecIndex].length;
        let allQuestionsAnswered = true;

        // ✅ 1️⃣ Ensure all SubQuestions are answered
        for (let i = 0; i < totalQuestions; i++) {
          const questionId =
            questions[currentSecIndex][i].attributes.question_id;
          if (!responses[questionId] || responses[questionId].length === 0) {
            allQuestionsAnswered = false;
            break;
          }
        }

        if (!allQuestionsAnswered) {
          // ✅ Move to first unanswered question
          for (let i = 0; i < totalQuestions; i++) {
            const questionId =
              questions[currentSecIndex][i].attributes.question_id;
            if (!responses[questionId] || responses[questionId].length === 0) {
              setCurrentQuestionIndex(i);
              setCurrentQuestionDisplay(currentQuestionDisplay + i + 1);
              setLoading(false);
              return;
            }
          }
        }

        // ✅ 2️⃣ Save all responses in SubQuestions
        for (let i = 0; i < totalQuestions; i++) {
          const questionId =
            questions[currentSecIndex][i].attributes.question_id;
          const transformedResponse = {
            questionnaire_id: questionId,
            response: JSON.stringify({
              value: responses[questionId]?.includes(-1)
                ? -1
                : responses[questionId]?.join(","),
            }),
            user_id: userId,
          };
          await diseaseAPI.saveScore(transformedResponse);
        }

        // ✅ 3️⃣ Move to the next section if all questions are answered
        if (currentSecIndex < questions.length - 1) {
          setCurrentQuestionIndex(0);
          setCurrentSectionIndex(currentSecIndex + 1);
          setCurrentQuestionDisplay(currentQuestionDisplay + totalQuestions);
        } else {
          await finalizeScore("Handle SubQuestions (Chips)");
        }
      }

      // ✅ Handle Dependent Questions (Fix: Skipping Multiple Questions)
      else if (currentSectionType === "Dependent") {
        const currentQuestionId =
          questions[currentSecIndex][currentQuesIndex].attributes.question_id;
        const parentQuestionId =
          questions[currentSecIndex][0].attributes.question_id;
        const parentQuestion = questionMap[parentQuestionId];

        const selectedIndices = responses[parentQuestionId] || [];
        const selectedValues = selectedIndices.map(
          (index: number) => parentQuestion.attributes.question_values[index]
        );

        // ✅ 1️⃣ Save Response for Parent Question at `currentQuesIndex === 0`

        const transformedResponse = {
          questionnaire_id: currentQuestionId,
          response: JSON.stringify({
            value: responses[currentQuestionId]?.join(","),
          }),
          user_id: userId,
        };
        await diseaseAPI.saveScore(transformedResponse);

        let nextQuestionIndex = currentQuesIndex + 1;
        let nextSectionIndex = currentSecIndex;
        let nextQuestionDisplay = currentQuestionDisplay + 1;

        // ✅ 2️⃣ Iterate Over Dependent Questions
        for (
          let i = nextQuestionIndex;
          i < questions[currentSecIndex].length;
          i++
        ) {
          const dependentQ = questions[currentSecIndex][i].attributes;
          const dependentQuestionId = dependentQ.question_id;

          // ✅ 3️⃣ Ensure Data Type Consistency for Comparison
          const normalizedSelectedValues = selectedValues.map(String); // Convert to String
          const normalizedDependentValue = String(
            dependentQ.dependent_question_value
          );

          // ✅ 4️⃣ Check if Parent Selection Matches `dependent_question_value`
          if (!normalizedSelectedValues.includes(normalizedDependentValue)) {
            const skippedResponse = {
              questionnaire_id: dependentQuestionId,
              response: JSON.stringify({ value: -1 }),
              user_id: userId,
            };
            await diseaseAPI.saveScore(skippedResponse);
            responses[dependentQuestionId] = [-1]; // Store locally
            continue; // Move to next dependent question
          }

          // ✅ 6️⃣ Move to Next Matched Dependent Question
          setCurrentQuestionIndex(i);
          setCurrentSectionIndex(currentSecIndex);
          setCurrentQuestionDisplay(nextQuestionDisplay);
          setLoading(false);
          return;
        }

        // ✅ 7️⃣ If No Matching Dependent Question → Move to Next Section
        if (nextSectionIndex < questions.length - 1) {
          setCurrentQuestionIndex(0);
          setCurrentSectionIndex(nextSectionIndex + 1);
          setCurrentQuestionDisplay(nextQuestionDisplay);
        } else {
          await finalizeScore(
            "If No Matching Dependent Question → Move to Next Section"
          );
        }
      }
    } catch (error) {
      console.error("🚨 Error in handleMoveNextQuestion:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMovePreviousQuestion = async (
    currentSecIndex: number,
    currentQuesIndex: number
  ) => {
    if (loading) return;
    setLoading(true);
    try {
      let currentSectionType = getSectionType(questions[currentSecIndex]);

      // ✅ 1️⃣ Handle Edge Case: First Question of First Section
      if (currentSecIndex === 0 && currentQuesIndex === 0) {
        await finalizeScore("Edge Case: First Question of First Section");
        return;
      }

      // ✅ 2️⃣ Handle Normal Question Navigation
      if (currentSectionType === "Normal") {
        if (currentQuesIndex > 0) {
          setCurrentQuestionIndex(currentQuesIndex - 1);
          setCurrentQuestionDisplay(currentQuestionDisplay - 1);
          setLoading(false);
          return;
        } else {
          moveToPreviousSection(currentSecIndex);
        }
      }

      // ✅ 3️⃣ Handle SubQuestions (Chips) Navigation
      else if (currentSectionType === "SubQuestions") {
        if (currentQuesIndex > 0) {
          setCurrentQuestionIndex(currentQuesIndex - 1);
          setCurrentQuestionDisplay(currentQuestionDisplay - 1);
          setLoading(false);
          return;
        } else {
          moveToPreviousSection(currentSecIndex);
        }
      }

      // ✅ 4️⃣ Handle Dependent Question Navigation (FIXED)
      else if (currentSectionType === "Dependent") {
        const parentQuestionId =
          questions[currentSecIndex][0].attributes.question_id;
        const parentQuestion = questionMap[parentQuestionId];

        const selectedIndices = mappedList[parentQuestionId] || [];
        const selectedValues = selectedIndices.map(
          (index: number) => parentQuestion.attributes.question_values[index]
        );

        // ✅ Case: Move Back to a Previous Dependent Question
        if (currentQuesIndex > 0) {
          for (let i = currentQuesIndex - 1; i >= 0; i--) {
            const dependentQ = questions[currentSecIndex][i].attributes;
            if (selectedValues.includes(dependentQ.dependent_question_value)) {
              setCurrentQuestionIndex(i);
              setCurrentQuestionDisplay(currentQuestionDisplay - 1);
              setLoading(false);
              return;
            }
          }
        }

        // ✅ Case: Move Back to Parent Question if No Matching Dependent Found
        if (currentQuesIndex === 0) {
          moveToPreviousSection(currentSecIndex);
        } else {
          setCurrentQuestionIndex(0);
          setCurrentQuestionDisplay(currentQuestionDisplay - 1);
        }
      }
    } catch (error) {
      console.error("🚨 Error in handleMovePreviousQuestion:", error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Moves to the Previous Section
  const moveToPreviousSection = (currentSecIndex: number) => {
    if (currentSecIndex === 0) return; // 🚨 Edge Case: First Section

    const prevSectionIndex = currentSecIndex - 1;
    const prevSectionType = getSectionType(questions[prevSectionIndex]);

    if (prevSectionType === "Normal") {
      // ✅ Move to last question in previous Normal section
      setCurrentSectionIndex(prevSectionIndex);
      setCurrentQuestionIndex(questions[prevSectionIndex].length - 1);
      setCurrentQuestionDisplay(currentQuestionDisplay - 1);
    } else if (prevSectionType === "SubQuestions") {
      // ✅ Move to last question in previous SubQuestions section
      setCurrentSectionIndex(prevSectionIndex);
      // setCurrentQuestionIndex(questions[prevSectionIndex].length - 1);
      setCurrentQuestionIndex(0);
      setCurrentQuestionDisplay(
        currentQuestionDisplay - questions[prevSectionIndex].length
      );
    } else if (prevSectionType === "Dependent") {
      // ✅ Move to the last Dependent question that matches Parent selection
      const parentQuestionId =
        questions[prevSectionIndex][0].attributes.question_id;
      const parentQuestion = questionMap[parentQuestionId];

      const selectedIndices = mappedList[parentQuestionId] || [];
      const selectedValues = selectedIndices.map(
        (index: number) => parentQuestion.attributes.question_values[index]
      );

      // ✅ Find the LAST question in the Dependent section that matches `dependent_question_value`
      for (let i = questions[prevSectionIndex].length - 1; i >= 0; i--) {
        const dependentQ = questions[prevSectionIndex][i].attributes;
        if (selectedValues.includes(dependentQ.dependent_question_value)) {
          setCurrentSectionIndex(prevSectionIndex);
          setCurrentQuestionIndex(i);
          setCurrentQuestionDisplay(currentQuestionDisplay - 1);
          return;
        }
      }

      // ✅ If no matching dependent question found, move to Parent Question
      setCurrentSectionIndex(prevSectionIndex);
      setCurrentQuestionIndex(0);
      setCurrentQuestionDisplay(currentQuestionDisplay - 1);
    }
  };

  const getNextScore = () => {
    // 2. mark it as filled
    if (selectedScore) {
      markScoreAsFilled(selectedScore);
    }

    // 3. Make a copy of healthScores and update the hasFilled property for the selected score
    const updatedHealthScores = healthScores[0].map((score) => {
      if (score.name === selectedScore) {
        return { ...score, hasFilled: true };
      }
      return score;
    });

    // 4. find the next score which is not filled from the updatedHealthScores
    let nextScore = null;
    for (let i = 0; i < updatedHealthScores.length; i++) {
      if (!updatedHealthScores[i].hasFilled) {
        nextScore = updatedHealthScores[i].name;
        break;
      }
    }

    // 5. If found set the next score to nextScore or else set null
    setNextScore(nextScore);
  };

  const finalizeScore = async (from?: string) => {
    try {
      getNextScore();
      await diseaseAPI.getScoreLabels(userId!, selectedScore);
      if (currentSectionIndex >= 0) {
        await handleScoreReult(userId!, selectedScore!);
      }
      setShowResultDialog(true);
      setIsDialogOpen(false);
    } catch (error) {
      console.error("🚨 Error finalizing score:", error);
      throw Error("Something went wrong.");
    }
  };

  const handleScoreReult = async (userId: string, scoreName: string) => {
    try {
      if (scoreName === "vikriti") {
        const data = await getVikritiRecommendations(userId);

        setScoreResult(data);
      } else if (scoreName === "Prakriti") {
        const data = await diseaseAPI.getAllScores(userId);
        if (
          data &&
          data.recommendations &&
          data.recommendations[scoreName] &&
          Object.keys(data.recommendations[scoreName].recommendations).length >
            0
        ) {
          setScoreResult(data.recommendations[scoreName]);
        }
      } else if (scoreName === "Tongue Analysis") {
        const res: any = {
          "Tongue Analysis": {
            label: " Low Digestive Fire",
            recommendations: {
              label: " Low Digestive Fire",
              "About the Score & Risk": [
                "Your digestive fire is hampered.",
                "It refers to as ''agnimandya'' in ayurveda which means insufficient digestion of the ingested food.",
                "Symptoms of weak digestive fire (Agni) include excessive gas, bloating, a feeling of heaviness in both the abdomen and body, as well as irregular or incomplete bowel movements.",
              ],
              "Risk Factors": [
                "Triphala",
                "Chitrakadi Vati",
                "Panchkol Churna",
                "Hingwashtak Churna",
                "Dhanwantar Vati",
              ],
              Mitigation_Strategies_Heading: [
                "Opt for freshly cooked meals that are warm and easily digestible as well as avoid consuming curd at night and avoid mixing two cereals together.",
                "Take 3 gm Fennel seed powder should include 10 min prior to your meals.",
                "Drink warm water or herbal teas instead of cold beverages to support digestion and avoid drinking large amounts of water on an empty stomach first thing in the morning.",
                "Limit liquid intake during meals and just after the meals to allow for better digestion.",
                "Focus on your food while eating, chew thoroughly, and avoid distractions.",
                "Engage in regular, moderate exercise to stimulate digestion and overall metabolism.",
                "Incorporate stress-reducing practices like meditation or yoga, as stress can negatively impact digestive fire.",
                "Practice deep breathing exercises to promote relaxation and enhance digestive function.",
                "Consider periodic detoxification practices, like Panchakarma, to reset and strengthen Agni under the observation of an Ayurvedic practitioner.",
                "Use herbal formulations to support and enhance Agni under the supervision of an Ayurvedic practitioner.",
              ],
            },
          },
        };
        setScoreResult(res["Tongue Analysis"]);
      } else {
        const data = await diseaseAPI.getAllScores(userId);
        if (
          data &&
          data.recommendations &&
          data.recommendations[scoreName] &&
          Object.keys(data.recommendations[scoreName].recommendations).length >
            0
        ) {
          setScoreResult(data.recommendations[scoreName]);
        }
      }
    } catch (error) {
      console.error("Error getting Vikriti Recomendation:", error);
    } finally {
    }
  };

  /*
    Two fixes:
    1. When we call handleMoveNextQuestion on any chips question then we need to call saveScore api for at most 3 times. Because we are getting the whole section at a time. So we need to save the response for all the questions in the section. Or else call it only once
    2. When we are in the dependent question section and that is the last section and we skipped questions then we don;t get he chance of calling this function again. So in that case we have to call the saveScore api for the skipped questions and at the last call the call scorelabel api and close the dialog.
  */

  return {
    selectedScore,
    questions,
    mappedList,
    questionMap,
    questionArrayLength,
    currentSectionIndex,
    currentQuestionIndex,
    currentQuestionDisplay,
    isDialogOpen,
    handleSelectScore,
    setSelectedScore,
    userScores,
    setUserScores,
    scoreResultLoading,
    setScoreResultLoading,
    scoreResult,
    setScoreResult,
    handleScoreReult,
    handleMoveNextQuestion,
    loading,
    setLoading,
    setMappedList,
    handleMovePreviousQuestion,
    showResultDialog,
    setShowResultDialog,
    isInitialLoad,
    setIsInitialLoad,
    allScoresData,
    setAllScoresData,
    healthScores,
    setHealthScores,
    markScoreAsFilled,
    healthHistoryCount,
    setHealthHistoryCount,
    nextScore,
    setNextScore,
    getNextScore,
    reviewResponse,
    setReviewResponse,
    scoreLabelCalledFor,
    setScoreLabelCalledFor,
    callFunction,
    setCallFunction,
    refetchScore,
    setRefetchScore,
  };
};

interface Props {
  children: React.ReactNode;
}

export const ScoresStateProvider = ({ children }: Props) => {
  const app = useScoresStateProvider();

  return (
    <questionContext.Provider value={app}>{children}</questionContext.Provider>
  );
};

export const useScoreState = () => {
  return useContext(questionContext);
};
